'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, User, Calendar, Palette, ChevronDown, ChevronUp } from 'lucide-react';
import Image from 'next/image';
import { Gender } from '@/utils/ai-types';

interface CharacterDetailsReviewStepProps {
  photoUrl?: string;
  childName?: string;
  childAge?: string;
  childGender?: Gender;
  aiAnalysis?: {
    gender?: string;
    age?: number;
    estimatedAge?: number;  // Some APIs return estimatedAge instead of age
    hairColor?: string;
    hairStyle?: string;
    skinColor?: string;
    eyeColor?: string;
    hairTexture?: string;
    confidence?: number;
  };
  onNameChange: (name: string) => void;
  onAgeChange: (age: string) => void;
  onGenderChange: (gender: Gender) => void;
  onAppearanceUpdate: (updates: any) => void;
  errors?: {
    name?: string;
    age?: string;
    gender?: string;
  };
}

export function CharacterDetailsReviewStep({
  photoUrl,
  childName,
  childAge,
  childGender,
  aiAnalysis,
  onNameChange,
  onAgeChange,
  onGenderChange,
  onAppearanceUpdate,
  errors
}: CharacterDetailsReviewStepProps) {
  const [showAppearanceDetails, setShowAppearanceDetails] = useState(false);
  
  // Helper function to map hex colors back to simple names
  const hexToColorName = (hex: string): string => {
    const colorMap: Record<string, string> = {
      '#000000': 'black',
      '#4a2c2a': 'brown',
      '#e6be8a': 'blonde',
      '#8d4a43': 'red',
      '#922724': 'auburn',
      '#d0d0d0': 'gray',
      '#ffffff': 'white',
      // Skin tones
      '#ffe0bd': 'light',
      '#e5c298': 'medium',
      '#c68642': 'olive',
      '#8d5524': 'brown',
      '#593d26': 'dark'
    };
    
    // Return the color name if found, otherwise try to extract from the value
    const lowerHex = (hex || '').toLowerCase();
    return colorMap[lowerHex] || lowerHex.replace('#', '');
  };
  
  // Appearance state - initialize with AI analysis or defaults
  const [appearance, setAppearance] = useState({
    gender: aiAnalysis?.gender || childGender || 'MALE',
    hairColor: hexToColorName(aiAnalysis?.hairColor) || 'brown',
    hairStyle: aiAnalysis?.hairStyle || 'short',
    skinTone: hexToColorName(aiAnalysis?.skinColor) || 'medium',
    eyeColor: aiAnalysis?.eyeColor || 'brown',
    hairTexture: aiAnalysis?.hairTexture || 'straight'
  });

  // Auto-set values from AI analysis
  useEffect(() => {
    console.log('[CharacterDetailsReview] AI Analysis received:', aiAnalysis);
    console.log('[CharacterDetailsReview] Current values:', { childAge, childGender });
    
    if (!aiAnalysis) return;
    
    // Auto-set age if detected and not already set
    const detectedAge = aiAnalysis.age || aiAnalysis.estimatedAge;
    if (detectedAge && !childAge) {
      const age = Math.round(detectedAge);
      const clampedAge = Math.min(Math.max(age, 1), 13);
      console.log('[CharacterDetailsReview] Setting age to:', clampedAge);
      onAgeChange(clampedAge.toString());
    }
    
    // Auto-set gender if detected
    if (aiAnalysis.gender && !childGender) {
      const mappedGender = aiAnalysis.gender.toUpperCase() === 'FEMALE' ? Gender.FEMALE : 
                          aiAnalysis.gender.toUpperCase() === 'MALE' ? Gender.MALE : 
                          Gender.OTHER;
      console.log('[CharacterDetailsReview] Setting gender to:', mappedGender);
      onGenderChange(mappedGender);
    }
    
    // Update appearance with AI results
    const newAppearance = {
      gender: aiAnalysis.gender?.toUpperCase() || appearance.gender,
      hairColor: hexToColorName(aiAnalysis.hairColor) || appearance.hairColor,
      hairStyle: aiAnalysis.hairStyle || appearance.hairStyle,
      skinTone: hexToColorName(aiAnalysis.skinColor) || appearance.skinTone,
      eyeColor: aiAnalysis.eyeColor || appearance.eyeColor,
      hairTexture: aiAnalysis.hairTexture || appearance.hairTexture
    };
    
    console.log('[CharacterDetailsReview] Updating appearance to:', newAppearance);
    setAppearance(newAppearance);
    onAppearanceUpdate(newAppearance);
  }, [aiAnalysis?.confidence]); // Only trigger when confidence changes (i.e., when new analysis arrives)


  const handleAppearanceUpdate = (field: string, value: string) => {
    const newAppearance = { ...appearance, [field]: value };
    setAppearance(newAppearance);
    onAppearanceUpdate({ [field]: value });
    
    // Update gender in main form if that's what changed
    if (field === 'gender') {
      const mappedGender = value.toUpperCase() === 'FEMALE' ? Gender.FEMALE : 
                          value.toUpperCase() === 'MALE' ? Gender.MALE : 
                          Gender.OTHER;
      onGenderChange(mappedGender);
    }
  };

  // Gender options
  const genderOptions = [
    { id: 'MALE', label: 'Boy', emoji: '👦', color: 'blue' },
    { id: 'FEMALE', label: 'Girl', emoji: '👧', color: 'pink' },
    { id: 'OTHER', label: 'Other', emoji: '🌟', color: 'purple' }
  ];

  // Hair colors - map to match what AI returns
  const hairColors = [
    { id: 'black', color: '#1a1a1a', label: 'Black' },
    { id: 'brown', color: '#6B4423', label: 'Brown' },
    { id: 'blonde', color: '#F4D03F', label: 'Blonde' },
    { id: 'red', color: '#B73E3E', label: 'Red' },
    { id: 'gray', color: '#9CA3AF', label: 'Gray' }
  ];

  // Skin tones - map to match what AI returns
  const skinTones = [
    { id: 'light', color: '#FDEBD0', label: 'Light' },
    { id: 'medium', color: '#D2B48C', label: 'Medium' },
    { id: 'dark', color: '#8B4513', label: 'Dark' }
  ];
  
  // Eye colors
  const eyeColors = [
    { id: 'blue', color: '#4A90E2', label: 'Blue' },
    { id: 'green', color: '#7ED321', label: 'Green' },
    { id: 'brown', color: '#8B4513', label: 'Brown' },
    { id: 'hazel', color: '#8E7618', label: 'Hazel' },
    { id: 'gray', color: '#9CA3AF', label: 'Gray' },
    { id: 'black', color: '#1a1a1a', label: 'Black' }
  ];
  
  // Hair textures
  const hairTextures = [
    { id: 'straight', label: 'Straight', icon: '➖' },
    { id: 'wavy', label: 'Wavy', icon: '〰️' },
    { id: 'curly', label: 'Curly', icon: '🌀' },
    { id: 'coily', label: 'Coily', icon: '🔄' }
  ];

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Photo thumbnail with AI status */}
      {photoUrl && (
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-32 h-32 rounded-2xl overflow-hidden shadow-lg bg-gray-100">
              <Image
                src={photoUrl}
                alt="Child's photo"
                fill
                style={{ objectFit: 'cover' }}
                priority
              />
            </div>
            {aiAnalysis && aiAnalysis.confidence && aiAnalysis.confidence > 0.5 && (
              <div className="absolute -bottom-2 -right-2 bg-purple-500 text-white rounded-full p-2 shadow-lg">
                <Sparkles size={16} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Analysis Status */}
      {aiAnalysis && aiAnalysis.confidence && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center"
        >
          <p className="text-sm text-purple-800 flex items-center justify-center">
            <Sparkles className="mr-2" size={16} />
            We've analyzed the photo and pre-filled some details
          </p>
        </motion.div>
      )}

      {/* Character Name */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
          <User className="w-4 h-4 mr-2" />
          Child's Name
        </label>
        <input
          type="text"
          value={childName || ''}
          onChange={(e) => onNameChange(e.target.value.trim())}
          onBlur={(e) => onNameChange(e.target.value.trim())}
          className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
          placeholder="Enter your child's first name"
          autoFocus
        />
        {errors?.name && (
          <p className="text-red-500 text-sm mt-1">{errors.name}</p>
        )}
      </div>

      {/* Age Selection */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
          <Calendar className="w-4 h-4 mr-2" />
          Age
        </label>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((age) => (
            <button
              key={age}
              type="button"
              onClick={() => onAgeChange(age.toString())}
              className={`py-3 px-4 rounded-xl font-semibold transition-all ${
                childAge === age.toString()
                  ? 'bg-purple-600 text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {age}
            </button>
          ))}
          <button
            type="button"
            onClick={() => onAgeChange('13')}
            className={`py-3 px-4 rounded-xl font-semibold transition-all ${
              childAge === '13'
                ? 'bg-purple-600 text-white shadow-lg scale-105'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            13+
          </button>
        </div>
        {errors?.age && (
          <p className="text-red-500 text-sm mt-1">{errors.age}</p>
        )}
      </div>

      {/* Gender Selection */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Gender
        </label>
        <div className="grid grid-cols-3 gap-3">
          {genderOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => handleAppearanceUpdate('gender', option.id)}
              className={`relative p-4 rounded-xl transition-all ${
                appearance.gender === option.id || childGender === option.id
                  ? `bg-${option.color}-500 text-white shadow-lg scale-105`
                  : 'bg-white border-2 border-gray-200 hover:border-purple-300'
              }`}
              style={
                appearance.gender === option.id || childGender === option.id
                  ? {
                      backgroundColor: option.color === 'blue' ? '#3B82F6' : 
                                       option.color === 'pink' ? '#EC4899' : 
                                       '#9333EA'
                    }
                  : {}
              }
            >
              <div className="text-3xl mb-1">{option.emoji}</div>
              <div className={`font-medium ${
                appearance.gender === option.id || childGender === option.id
                  ? 'text-white' 
                  : 'text-gray-700'
              }`}>
                {option.label}
              </div>
              {(appearance.gender === option.id || childGender === option.id) && (
                <Check className="absolute top-2 right-2 w-5 h-5" />
              )}
            </button>
          ))}
        </div>
        {errors?.gender && (
          <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
        )}
      </div>

      {/* Appearance Details (Collapsible) */}
      <div className="border-t pt-4">
        <button
          type="button"
          onClick={() => setShowAppearanceDetails(!showAppearanceDetails)}
          className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
        >
          <div className="flex items-center">
            <Palette className="w-5 h-5 mr-2 text-purple-600" />
            <span className="font-medium text-gray-700">Appearance Details</span>
            {aiAnalysis && (
              <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                Detected
              </span>
            )}
          </div>
          {showAppearanceDetails ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </button>

        <AnimatePresence>
          {showAppearanceDetails && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 space-y-4">
                {/* Hair Color */}
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">Hair Color</label>
                  <div className="flex gap-2">
                    {hairColors.map((color) => (
                      <button
                        key={color.id}
                        type="button"
                        onClick={() => handleAppearanceUpdate('hairColor', color.id)}
                        className={`relative p-1 rounded-lg transition-all ${
                          appearance.hairColor === color.id
                            ? 'ring-2 ring-purple-500 ring-offset-2'
                            : 'hover:scale-110'
                        }`}
                      >
                        <div
                          className="w-10 h-10 rounded-lg shadow-inner"
                          style={{ backgroundColor: color.color }}
                        />
                        {appearance.hairColor === color.id && (
                          <Check className="absolute -top-1 -right-1 w-4 h-4 text-purple-600 bg-white rounded-full" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Hair Texture */}
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">Hair Texture</label>
                  <div className="flex gap-2">
                    {hairTextures.map((texture) => (
                      <button
                        key={texture.id}
                        type="button"
                        onClick={() => handleAppearanceUpdate('hairTexture', texture.id)}
                        className={`px-3 py-2 rounded-lg transition-all flex items-center gap-1 ${
                          appearance.hairTexture === texture.id
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        <span className="text-lg">{texture.icon}</span>
                        <span className="text-sm font-medium">{texture.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Skin Tone */}
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">Skin Tone</label>
                  <div className="flex gap-2">
                    {skinTones.map((tone) => (
                      <button
                        key={tone.id}
                        type="button"
                        onClick={() => handleAppearanceUpdate('skinTone', tone.id)}
                        className={`relative p-1 rounded-lg transition-all ${
                          appearance.skinTone === tone.id
                            ? 'ring-2 ring-purple-500 ring-offset-2'
                            : 'hover:scale-110'
                        }`}
                      >
                        <div
                          className="w-12 h-12 rounded-lg shadow-inner"
                          style={{ backgroundColor: tone.color }}
                        />
                        {appearance.skinTone === tone.id && (
                          <Check className="absolute -top-1 -right-1 w-4 h-4 text-purple-600 bg-white rounded-full" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Eye Color */}
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">Eye Color</label>
                  <div className="flex gap-2 flex-wrap">
                    {eyeColors.map((color) => (
                      <button
                        key={color.id}
                        type="button"
                        onClick={() => handleAppearanceUpdate('eyeColor', color.id)}
                        className={`relative p-1 rounded-lg transition-all ${
                          appearance.eyeColor === color.id
                            ? 'ring-2 ring-purple-500 ring-offset-2'
                            : 'hover:scale-110'
                        }`}
                      >
                        <div
                          className="w-10 h-10 rounded-full shadow-inner border-2 border-gray-200"
                          style={{ backgroundColor: color.color }}
                        />
                        {appearance.eyeColor === color.id && (
                          <Check className="absolute -top-1 -right-1 w-4 h-4 text-purple-600 bg-white rounded-full" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
